import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { usePropertyPanel } from '../../propertyPanel/PropertyPanelContext';
import { useTranslation } from '../../../redux/TranslationHelper';

interface CartPropertiesProps {
  // 필요한 경우 추가 속성을 정의합니다
}

const CartProperties: React.FC<CartPropertiesProps> = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // getDevicePropertyValue는 현재 사용되지 않으므로 eslint-disable-next-line 주석 추가
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { devicePropertyTab, getDevicePropertyValue } = usePropertyPanel();
  
  // 컴포넌트 속성 변경 핸들러 - 컴포넌트의 최상위 속성에 직접 저장
  const handleComponentPropsChange = (property: string, value: any) => {
    // 최상위 속성으로 업데이트
    const updatedComponent = JSON.parse(JSON.stringify(selectedComponent));
    
    // 디바이스 속성 탭이 선택된 경우, 해당 디바이스에 대한 속성만 변경
    if (devicePropertyTab) {
      // 디바이스별 객체 초기화
      if (!updatedComponent.deviceProperty) {
        updatedComponent.deviceProperty = {
          pc: {},
          mobile: {}
        };
        
        // 기존 속성을 PC/모바일에 복사
        Object.keys(updatedComponent).forEach(key => {
          if (key !== 'deviceProperty' && !['id', 'type', 'content', 'style'].includes(key)) {
            updatedComponent.deviceProperty.pc[key] = updatedComponent[key];
            updatedComponent.deviceProperty.mobile[key] = updatedComponent[key];
          }
        });
      }
      
      // 선택된 디바이스 탭에 속성 업데이트
      if (!updatedComponent.deviceProperty[devicePropertyTab]) {
        updatedComponent.deviceProperty[devicePropertyTab] = {};
      }
      
      updatedComponent.deviceProperty[devicePropertyTab][property] = value;
      
      // PC 모드일 때만 최상위 속성에도 적용
      if (devicePropertyTab === 'pc') {
        updatedComponent[property] = value;
      }
    } else {
      // 최상위 속성에 직접 업데이트
      updatedComponent[property] = value;
      
      // 디바이스별 속성도 업데이트 (있는 경우)
      if (updatedComponent.deviceProperty) {
        if (updatedComponent.deviceProperty.pc) {
          updatedComponent.deviceProperty.pc[property] = value;
        }
        if (updatedComponent.deviceProperty.mobile) {
          updatedComponent.deviceProperty.mobile[property] = value;
        }
      }
    }
    
    // 컴포넌트 전체 상태 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: updatedComponent
    }));
    
    // 전역 이벤트 발생 - 현재 속성값이 변경되었음을 알림
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('propertyChanged', {
        detail: {
          deviceType: devicePropertyTab || 'all',
          componentId: selectedComponent.id,
          property,
          value
        }
      }));
    }
  };

  // 장바구니 속성 가져오기
  const getValue = (property: string, defaultValue: any) => {
    // 디바이스별 속성값 먼저 확인
    if (devicePropertyTab && 
        selectedComponent.deviceProperty?.[devicePropertyTab]?.[property] !== undefined) {
      return selectedComponent.deviceProperty[devicePropertyTab][property];
    }
    
    // 최상위 속성 확인
    if (selectedComponent[property] !== undefined) {
      return selectedComponent[property];
    }
    
    return defaultValue;
  };

  return (
    <>
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 3H5.5L6 5M6 5H21L17 14H8M6 5L8 14M8 14L6.5 19H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="8.5" cy="21.5" r="1.5" stroke="currentColor"/>
            <circle cx="17.5" cy="21.5" r="1.5" stroke="currentColor"/>
          </svg>
          {t('장바구니 설정')}
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>{t('타이틀')}</label>
          <input 
            type="text"
            value={getValue('title', t('장바구니'))}
            onChange={(e) => handleComponentPropsChange('title', e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>{t('빈 장바구니 메시지')}</label>
          <input 
            type="text"
            value={getValue('emptyText', t('장바구니가 비어 있습니다.'))}
            onChange={(e) => handleComponentPropsChange('emptyText', e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>{t('배송비 (원)')}</label>
          <input 
            type="number"
            min="0"
            value={getValue('shippingFee', 4000)}
            onChange={(e) => handleComponentPropsChange('shippingFee', parseInt(e.target.value, 10))}
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>{t('재고 정보 표시')}</label>
          <div className="custom-toggle-switch">
            <input 
              id="toggle-stock-cart"
              type="checkbox" 
              checked={getValue('showStock', false)}
              onChange={(e) => handleComponentPropsChange('showStock', e.target.checked)}
            />
            <label htmlFor="toggle-stock-cart" className="toggle-switch-label">
              <span className="toggle-track"></span>
            </label>
          </div>
        </div>
      </div>
    </>
  );
};

export default CartProperties; 